<script setup>
import {
  Building2,
  ShieldCheck,
  MapPin,
  Receipt,
  ArrowRight,
  Check
} from 'lucide-vue-next'
import businessOwner from '../../assets/img/business-owner-llevo.webp'

const benefits = [
  {
    icon: ShieldCheck,
    title: 'Acceso a repartidores verificados',
    description: 'Tus envíos son asignados de forma automática al repartidor disponible más cercano, todos identificados y registrados en nuestra plataforma. Tu cliente recibe confianza y tu marca, una experiencia profesional sin que tengas que gestionar personal.'
  },
  {
    icon: MapPin,
    title: 'Seguimiento del recorrido en tiempo real',
    description: 'Tú y tu cliente pueden ver el trayecto del repartidor asignado en el mapa desde que recoge el paquete hasta que lo entrega. Menos llamadas de consulta, más control y visibilidad en cada servicio.'
  },
  {
    icon: Receipt,
    title: 'Sin comisiones sorpresa ni cargos ocultos',
    description: 'Sabes el costo exacto por zona y distancia antes de confirmar el despacho. Sin contratos de exclusividad, sin cuotas mensuales y sin comisiones que asfixien tu ganancia al cierre del día.'
  }
]

</script>

<template>
  <section id="negocios" class="relative scroll-mt-24 overflow-hidden bg-[#f7f8fa] py-20 md:py-28">
    <div class="pointer-events-none absolute -left-40 top-20 h-[24rem] w-[24rem] rounded-full bg-[#f0611e]/[0.05] blur-[110px]"></div>

    <div class="container relative z-10 mx-auto px-6 lg:px-12">
      <!-- Foto + copy principal -->
      <div class="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
        <div v-reveal class="lg:col-span-4">
          <div class="relative mx-auto max-w-md lg:max-w-none">
            <div class="relative overflow-hidden rounded-[2rem] bg-slate-100 shadow-panel">
              <img
                :src="businessOwner"
                alt="Comerciante entregando un pedido de LLEVO a un repartidor"
                class="aspect-[4/5] w-full object-cover"
                loading="lazy"
                decoding="async"
              >
              <span class="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-[#f0611e] px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-lg">
                <Building2 :size="12" />
                Para negocios
              </span>
            </div>
          </div>

          <div class="mx-auto mt-4 max-w-md lg:max-w-none">
            <p class="text-sm font-black text-[#2b4162]">Tu marca, en cada entrega.</p>
            <p class="mt-1 text-xs leading-relaxed text-slate-600">
              Repartidores verificados y seguimiento en tiempo real.
            </p>
          </div>
        </div>

        <div v-reveal="{ delay: 120 }" class="lg:col-span-8">
          <p class="text-[11px] font-black uppercase tracking-[0.18em] text-[#c2410c]">Para negocios</p>

          <h2 class="mt-4 text-balance text-3xl font-black tracking-[-0.03em] text-[#2b4162] sm:text-4xl md:text-[3rem] xl:text-5xl">
            Suma entregas a tu negocio
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#f0611e] to-[#d85317]">sin contratar personal ni vehículos.</span>
          </h2>

          <p class="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Accede a una red activa de repartidores independientes y listos para despachar tus pedidos cuando lo necesites.
            Sin costos fijos, sin planillas y pagando solo por cada envío realizado.
          </p>

          <div class="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <router-link
              to="/postular-negocio"
              class="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#2b4162] px-5 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1c2b42]"
            >
              <Building2 :size="16" />
              Quiero hacer crecer mi negocio
              <ArrowRight :size="16" class="transition-transform group-hover:translate-x-1" />
            </router-link>
            <a href="#como-funciona" class="inline-flex items-center gap-2 text-sm font-bold text-[#2b4162] transition-colors hover:text-[#c2410c]">
              Ver cómo funciona
              <ArrowRight :size="15" />
            </a>
          </div>
        </div>
      </div>

      <!-- Beneficios -->
      <div class="mt-12 grid gap-4 md:grid-cols-3">
        <article
          v-for="(benefit, index) in benefits"
          :key="benefit.title"
          v-reveal="{ delay: index * 80 }"
          class="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#f0611e]/30 hover:shadow-panel lg:p-7"
        >
          <div class="flex items-center">
            <span class="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff1ea] text-[#c2410c] transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3">
              <component :is="benefit.icon" :size="20" />
            </span>
          </div>

          <h3 class="mt-6 text-lg font-black leading-snug tracking-tight text-[#2b4162]">{{ benefit.title }}</h3>
          <p class="mt-2 text-sm leading-relaxed text-slate-600">{{ benefit.description }}</p>
        </article>
      </div>

      <p class="mt-6 flex items-start gap-2 text-xs leading-relaxed text-slate-600">
        <Check :size="14" class="mt-0.5 shrink-0 text-[#c2410c]" />
        Ideal para restaurantes, farmacias, tiendas de barrio, distribuidores y e-commerce.
      </p>
    </div>
  </section>
</template>
